/**
 * Responsible for validating an email field with specific messaging based on
 * what is specifically incorrect with the field.
 * @module elements/fields/validate-email
 */
import $ from 'jquery';
// import isEmail from 'validator/lib/isEmail';
import isFQDN from 'validator/lib/isFQDN';
import find from 'lodash/find';
import get from 'lodash/get';

export const NS = 'validate-email';

const invalidMessaging = {
  i18nKey: 'isInvalid',
  message: 'Email is invalid',
};

const validationPriorities = [
  // When the preceived username is a domain, i.e. an @ symbol is forgotten
  {
    condition: username => isFQDN(username),
    i18nKey: 'requiresDomain',
    message: 'Email address needs an @',
  },

  // When no domain has been provided or a user has only entered a username
  Object.assign({}, invalidMessaging, {
    condition: (username, domain) => !domain,
  }),

  // When no TLD has been provided, i.e. foo@foo
  {
    condition: (username, domain) => {
      const [, tld] = domain.split('.');
      return !isFQDN(domain) && !tld;
    },
    i18nKey: 'requiresTLD',
    message: 'Email address needs a top level domain',
  },

  // Catch all for all usecases that do not meet the above criteria
  // Object.assign({}, invalidMessaging, {
  //   condition: (username, domain, email) => !isEmail(email),
  // }),
];

/**
 * Responsible for making the source element either valid or invalid.
 * @param {jQuery} $el - Element with reference to target
 * @param {string} message - The error message
 */
const verifyError = ($el, message = '') => {
  const source = $el[0];
  source.setCustomValidity(message);
  source.checkValidity();

  if (!message) {
    const evt = new CustomEvent('valid');
    source.dispatchEvent(evt);
  }
};

/**
 * Responsible for running through messages with the intention of returning
 * which type of validation has been found.
 * @param {jQuery} $el - Elements checked to be a valid email
 * @param {Object} options - Configurable options
 * @returns {string} Value used for setting custom validity
 */
export const validateEmail = ($el, options = {}) => {
  const email = $el.val();
  // Make sure we have a value just in case the field is not required. That
  // can be handled by another validator.
  if (!email) {
    return '';
  }

  const { global } = Object.assign({}, {
    global: window,
  }, options);

  const [username, domain = ''] = email.split('@');
  const validation = find(
    validationPriorities,
    ({ condition }) => condition(username, domain, email),
  );
  if (validation) {
    const { i18nKey, message } = validation;
    return get(global, ['rcProps', 'emailValidation', i18nKey], message);
  }

  return '';
};

/**
 * Responsible for applying email validation with specific error messages
 * based on a succession of validations.
 * @param {HTMLElement} el - Element receiving email validation
 * @param {Object} options - Configurable options
 */
export default (el, options = {}) => {
  const $el = $(el);

  $el.on(`blur.${NS}`, () => {
    const message = validateEmail($el, options);
    verifyError($el, message);
  });
};



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/validate-email.js